import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    CONNECTION_ERROR_CODES,
    ConnectivityService,
    isConnectionErrorCode,
    SYNC_REQUEST_TYPE,
    SynchronizationInterface,
    SyncQueueService,
    SyncRequestModel,
} from '@evorait/pace-core';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import { OfflineService } from '@evorait/pace-offline';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class SynchronizationService implements SynchronizationInterface {
    public constructor(
        private readonly connectivityService: ConnectivityService,
        private readonly offlineService: OfflineService,
        private readonly appNotificationService: AppNotificationService,
        private readonly syncQueueService: SyncQueueService,
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.connectivityService.isOnline$
            .pipe(
                filter((isOnline) => isOnline),
                mergeMap(() => this.checkSyncRequestQueue()),
            )
            .subscribe();
    }
    public sendOfflineDataAndReceiveNotificationsAndBootStock(): Observable<void> {
        throw new Error(
            `Method sendOfflineDataAndReceiveNotificationsAndBootStock shouldn't be triggered for pace forms for authors. method defined to follow the interface`,
        );
    }
    public flushCompletely(): Observable<boolean> {
        throw new Error(
            `Method flushCompletely shouldn't be triggered for pace forms for authors. method defined to follow the interface`,
        );
    }
    public receiveNotifications(triggerSyncEvent = true): Observable<void> {
        throw new Error(
            `Method receiveNotifications shouldn't be triggered for pace forms for authors. method defined to follow the interface and parameter ${triggerSyncEvent}`,
        );
    }

    public receiveWhatsNew(): Observable<void> {
        return of(undefined);
    }

    public sendOfflineData(): Observable<void> {
        return this.offlineService.isSyncing$.pipe(
            take(1),
            mergeMap((isSyncInProgress) => {
                if (isSyncInProgress) {
                    this.log.debug(`SynchronizationService: synchronize: sync in progress. Adding request to queue.`);
                    this.syncQueueService.addSyncRequest(SYNC_REQUEST_TYPE.FLUSH_ONLY, true);
                    return EMPTY;
                }

                return this.connectivityService.isOnlineButWithErrors$.pipe(
                    take(1),
                    mergeMap(() => this.offlineService.sendOfflineData().pipe(take(1))),
                    mergeMap(() => this.checkSyncRequestQueue()),
                    catchError((error: Error) => {
                        // Being here can have following reasons:
                        // - unknown error
                        // - no internet connection
                        // - no SAP connection
                        // - not authenticated
                        // - an offline plugin sync error
                        //
                        // For offline errors we dont call the handleSynchronizationPrerequisitesError as this would check the
                        // sync queue and try it again and end up in an endless loop. Therefore we just re-throw the error.
                        // Same for unknown errors - we simply do not know how to deal with them.
                        if (
                            !isConnectionErrorCode(error) ||
                            error === CONNECTION_ERROR_CODES.OFFLINE_PLUGIN_SYNC_ERROR
                        ) {
                            this.log.error(`SynchronizationService: synchronize: '${error?.message}'`);
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.WARN,
                                <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                                <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                            );
                            return throwError(() => error);
                        }

                        // If we can handle the error, let's do it :)
                        return this.handleSynchronizationPrerequisitesError(error);
                    }),
                );
            }),
        );
    }

    public receiveEverything(): Observable<void> {
        return of(undefined);
    }

    private checkSyncRequestQueue(): Observable<void> {
        let nextSyncRequest: SyncRequestModel;
        return this.syncQueueService.syncRequest$.pipe(
            take(1),
            mergeMap((syncRequests) => {
                if (syncRequests.length > 0) {
                    nextSyncRequest = syncRequests[0];
                    this.log.debug(
                        `SynchronizationService: checkSyncRequestQueue: ${nextSyncRequest.toString()} will be processed. Total ${
                            syncRequests.length
                        } sync requests`,
                    );
                    // Remove the request from sync queue BEFORE calling sendOffline
                    // as synchronize will again call this method and would end up in infinite loop
                    this.syncQueueService.removeSynchronizationRequest(nextSyncRequest);
                    return this.sendOfflineData();
                } else {
                    return of(undefined);
                }
            }),
            catchError((error: Error) => {
                this.log.error(
                    `SynchronizationService: checkSyncRequestQueue: failed for ${nextSyncRequest.toString()} with error: ${JSON.stringify(
                        error?.message,
                    )}`,
                );
                this.syncQueueService.addSyncRequest(nextSyncRequest.syncRequestType, nextSyncRequest.triggerSyncEvent);
                return of(undefined);
            }),
        );
    }

    /**
     * Handles error that deal with a missing internet connection or a not reachable Gateway or an unauthenticated user.
     * A missing internet connection will end gracefully with a resolve() which will tell the caller of synchronize()
     * that everything is fine so far, there is just no internet connection and no error message is show anywhere.
     *
     * Unauthenticated users have already triggered an authentication. Once the authentication finishes,
     * it will trigger the event "triggerAuthenticationFinished". The synchronization-service
     * will then check the request queue again. Therefore, we just resolve() in this case to tell the caller that everything
     * is fine so far and no error should be shown at this time.
     *
     * @param error code to be used in handling the synchronization prerequisites
     * @returns Observable<void>
     */
    private handleSynchronizationPrerequisitesError(error: CONNECTION_ERROR_CODES): Observable<void> {
        if (error === CONNECTION_ERROR_CODES.NO_INTERNET_CONNECTION) {
            this.log.debug(
                `SynchronizationService: handleSynchronizationPrerequisitesError: No internet connection detected. Adding request to queue.`,
            );
            this.syncQueueService.addSyncRequest(SYNC_REQUEST_TYPE.FLUSH_ONLY);
            return of(undefined);
        } else {
            this.log.error(`SynchronizationService: handleSynchronizationPrerequisitesError: ${error.toString()}`);
            return throwError(() => error);
        }
    }

    public receiveEverythingCacheFirst(): Observable<void> {
        return of(undefined);
    }
}
